/* Shopify-backed catalog and one-time routine studio. No tracking or invented stock. */
(() => {
  'use strict';
  if (window.GGCommerceExperience) return;
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollTo = element => element?.scrollIntoView({behavior: reducedMotion() ? 'instant' : 'smooth', block:'start'});
  async function addItems(root, items) {
    const response = await fetch(root.dataset.ggAddUrl, {
      method:'POST', credentials:'same-origin',
      headers:{'Content-Type':'application/json','Accept':'application/json'},
      body:JSON.stringify({items})
    });
    let data;
    try { data = await response.json(); }
    catch (_) { throw new Error('We could not confirm the cart update. Check your bag before trying again.'); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'A selected size could not be added. Check availability or choose another size.');
    // Existing storefront ATCs use this handoff: it avoids a slow asynchronous drawer refresh.
    window.location.assign(root.dataset.ggCartUrl || '/cart');
  }
  function setupRange(root) {
    if (root.dataset.ggExperienceReady) return;
    root.dataset.ggExperienceReady = 'true';
    const cards = [...root.querySelectorAll('[data-gg-range-card]')];
    const filterBar = root.querySelector('[data-gg-filter-bar]');
    const aliases = {soil:'soil',growth:'growing',growing:'growing',flower:'flower',flowering:'flower',supplements:'extras',extras:'extras',all:'all'};
    const publicRole = {soil:'soil',growing:'growth',flower:'flower',extras:'supplements'};
    function filter(requested, updateUrl = false) {
      const role = aliases[requested] || 'all';
      let count = 0;
      cards.forEach(card => { card.hidden = role !== 'all' && !card.dataset.ggFamily.split(' ').includes(role); if (!card.hidden) count++; });
      root.querySelectorAll('[data-gg-role]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.ggRole === role)));
      const countNode = root.querySelector('[data-gg-result-count]');
      if (countNode) countNode.textContent = `${count} ${count === 1 ? 'product' : 'products'}${root.querySelector('.ggx-pagination') ? ' on this page' : ''}`;
      const empty = root.querySelector('[data-gg-range-empty]');
      if (empty) empty.hidden = count > 0;
      if (updateUrl) {
        const url = new URL(location.href);
        if (role === 'all') url.searchParams.delete('role'); else url.searchParams.set('role',publicRole[role]);
        history.replaceState(null,'',url);
      }
    }
    if (filterBar) {
      filterBar.hidden = false;
      root.querySelectorAll('[data-gg-role]').forEach(button => button.addEventListener('click', () => filter(button.dataset.ggRole,true)));
      root.querySelector('[data-gg-reset-role]')?.addEventListener('click', () => filter('all',true));
      filter(new URLSearchParams(location.search).get('role'));
      window.addEventListener('popstate', () => filter(new URLSearchParams(location.search).get('role')));
    }
    root.querySelectorAll('[data-gg-quick-form]').forEach(form => form.addEventListener('submit', async event => {
      event.preventDefault();
      event.stopPropagation();
      if (form.dataset.busy) return;
      const button = form.querySelector('button[type="submit"]');
      const status = form.closest('[data-gg-range-card]').querySelector('[data-gg-card-status]');
      const id = new FormData(form).get('id');
      if (!id || button.disabled) return;
      form.dataset.busy = 'true';
      button.disabled = true;
      button.setAttribute('aria-busy','true');
      status.textContent = 'Adding to your bag…';
      try { await addItems(root,[{id:String(id),quantity:1}]); }
      catch (error) {
        status.textContent = error.message || 'Could not add this size. Please try again.';
        delete form.dataset.busy;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }));
  }
  function setupStudio(root) {
    if (root.dataset.ggExperienceReady) return;
    let config;
    try { config = JSON.parse(root.querySelector('[data-gg-studio-data]').textContent); }
    catch (_) { return; }
    root.dataset.ggExperienceReady = 'true';
    const products = config.products.filter(p => Array.isArray(p.variants) && p.variants.length);
    const byKey = key => products.find(p => p.key === key);
    const rows = new Map([...root.querySelectorAll('[data-gg-studio-row]')].map(row => [row.dataset.ggKey,row]));
    const formatter = new Intl.NumberFormat(config.locale || 'en-US',{style:'currency',currency:config.currency || 'USD'});
    const money = cents => formatter.format(cents / 100);
    const presets = {core:['microbes','veg','bloom'],extras:['microbes','veg','bloom','bulk','kelp'],full:products.map(p => p.key)};
    const titles = {core:'The core three.',extras:'Core + a little extra.',full:'The full lineup.',custom:'Your growing rhythm.',recommended:'Your starting point.'};
    const stageNames = {starting:'Getting started',growing:'Leaves & growth',flowering:'Flowers & fruit'};
    const purposeNames = {foundation:'A place to begin',supplement:'Something to add',learn:'A little guidance'};
    const state = {preset:'core',editing:false,busy:false,step:0,stage:'',purpose:'',recommendations:[],view:''};
    const addButton = root.querySelector('[data-gg-studio-add]');
    const status = root.querySelector('[data-gg-studio-status]');
    const panels = {finder:root.querySelector('#finder'),routine:root.querySelector('#routine')};
    const viewLinks = root.querySelectorAll('[data-gg-studio-view-link]');
    function showView(view,{scroll = false,focus = false,instant = false} = {}) {
      state.view = view;
      root.dataset.ggStudioView = view;
      Object.entries(panels).forEach(([key,panel]) => { panel.hidden = key !== view; });
      viewLinks.forEach(link => {
        if (link.dataset.ggStudioViewLink === view) link.setAttribute('aria-current','page');
        else link.removeAttribute('aria-current');
      });
      if (focus) panels[view].querySelector('h1')?.focus({preventScroll:true});
      if (scroll) requestAnimationFrame(() => {
        if (!root.isConnected || state.view !== view) return;
        panels[view].scrollIntoView({behavior:instant || reducedMotion() ? 'instant' : 'smooth',block:'start'});
      });
    }
    function navigateView(view) {
      const url = new URL(location.href);
      url.hash = view;
      if (url.href !== location.href) history.pushState(null,'',url);
      showView(view,{scroll:true,focus:true});
    }
    function selected() {
      return products.filter(p => rows.get(p.key)?.querySelector('[data-gg-product-check]').checked).map(p => {
        const row = rows.get(p.key), id = row.querySelector('[data-gg-row-variant]').value;
        return {product:p,variant:p.variants.find(v => String(v.id) === id)};
      });
    }
    function renderBuilder() {
      const lines = selected();
      products.forEach(p => {
        const row = rows.get(p.key);
        if (!row) return;
        const included = row.querySelector('[data-gg-product-check]').checked;
        const variant = p.variants.find(v => String(v.id) === row.querySelector('[data-gg-row-variant]').value);
        row.hidden = !included && !state.editing;
        row.classList.toggle('is-excluded',!included);
        const available = Boolean(variant?.available);
        row.querySelector('[data-gg-row-price]').textContent = variant ? money(variant.price) : 'Choose size';
        row.querySelector('[data-gg-row-stock]').textContent = available ? 'Available' : 'Currently unavailable';
        const notify = row.querySelector('[data-gg-row-notify]');
        notify.hidden = available || !variant;
        if (variant) { const url = new URL(p.url, location.origin); url.searchParams.set('variant',String(variant.id)); notify.href = url.href; }
        const image = row.querySelector('[data-gg-row-image]');
        if (image) { image.src = variant?.featured_image?.src || p.image; image.alt = `${p.name} product packaging`; }
      });
      root.querySelector('[data-gg-selection-label]').textContent = `${lines.length} ${lines.length === 1 ? 'product' : 'products'} on your shelf`;
      const intro = root.querySelector('[data-gg-builder-intro]');
      if (intro) intro.textContent = state.preset === 'recommended' ? 'Your suggested products are selected below. Choose your sizes and make this routine yours.' : 'Choose your products and sizes. Start with a collection, then make it yours.';
      const collectionHeading = root.querySelector('[data-gg-collection-heading]');
      if (collectionHeading) collectionHeading.textContent = state.preset === 'recommended' ? 'Explore other starting collections' : 'Choose a starting collection.';
      root.querySelector('[data-gg-builder-empty]').hidden = lines.length > 0;
      root.querySelector('[data-gg-summary-title]').textContent = titles[state.preset];
      root.querySelector('[data-gg-summary-lines]').innerHTML = lines.map(({product:p,variant:v}) => `<li><span>${esc(p.name)}<small>${esc(v?.title || 'Choose a size')} · Qty 1${v && !v.available ? ' · Unavailable' : ''}</small></span><strong>${v ? esc(money(v.price)) : '—'}</strong></li>`).join('');
      const total = lines.reduce((sum,line) => sum + (line.variant?.price || 0),0);
      root.querySelector('[data-gg-studio-total]').textContent = money(total);
      root.querySelector('[data-gg-studio-add-label]').textContent = `Add ${lines.length || 'selected'} ${lines.length === 1 ? 'product' : 'products'} to bag`;
      const unavailable = lines.filter(line => !line.variant?.available);
      addButton.disabled = state.busy || !lines.length || unavailable.length > 0;
      status.classList.remove('is-error');
      status.textContent = state.busy ? 'Adding your selected sizes…' : !lines.length ? 'Choose at least one product to begin.' : unavailable.length ? `${unavailable.map(line => line.product.name).join(', ')}: choose an available size or remove from your selection. Restock links are beside each product.` : 'Your selected sizes are available. One of each, one-time purchase.';
      root.querySelectorAll('[data-gg-routine]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.ggRoutine === state.preset)));
      const edit = root.querySelector('[data-gg-edit-products]');
      edit.setAttribute('aria-expanded',String(state.editing));
      edit.innerHTML = state.editing ? 'Done choosing <span aria-hidden="true">✓</span>' : 'Edit products <span aria-hidden="true">+</span>';
    }
    function choose(keys,preset) {
      if (state.busy) return;
      rows.forEach((row,key) => { row.querySelector('[data-gg-product-check]').checked = keys.includes(key); });
      state.preset = preset;
      renderBuilder();
    }
    root.querySelectorAll('[data-gg-routine]').forEach(button => button.addEventListener('click', () => choose(presets[button.dataset.ggRoutine],button.dataset.ggRoutine)));
    root.querySelector('[data-gg-edit-products]').addEventListener('click', () => { if (!state.busy) { state.editing = !state.editing; renderBuilder(); } });
    rows.forEach(row => {
      row.querySelector('[data-gg-product-check]').addEventListener('change', () => { state.preset = 'custom'; renderBuilder(); });
      row.querySelector('[data-gg-row-variant]').addEventListener('change',renderBuilder);
    });
    function showStep(number,focus = true) {
      state.step = number;
      root.querySelectorAll('[data-gg-finder-step]').forEach(step => { step.hidden = Number(step.dataset.ggFinderStep) !== number; });
      const progress = root.querySelector('[data-gg-progress]');
      progress.setAttribute('aria-label',number === 2 ? 'Your starting point' : `Question ${number + 1} of 2`);
      [...progress.children].forEach((part,i) => part.classList.toggle('is-done',i <= number));
      if (focus) root.querySelector(`[data-gg-finder-step="${number}"] :is(h2,h3)`).focus({preventScroll:true});
    }
    function recommend() {
      let keys;
      if (state.purpose === 'supplement') keys = state.stage === 'flowering' ? ['bulk','kelp'] : state.stage === 'growing' ? ['kelp','krustacean'] : ['microbes','kelp'];
      else keys = state.stage === 'flowering' ? ['bloom','microbes'] : ['veg','microbes'];
      state.recommendations = keys.filter(byKey);
      root.querySelector('[data-gg-finder-context]').textContent = `${stageNames[state.stage]} · ${purposeNames[state.purpose]}`;
      root.querySelector('[data-gg-finder-note]').textContent = state.purpose === 'supplement' ? 'These products have different roles alongside a feeding routine. Explore their labels and what your existing feed supplies before adding anything.' : state.stage === 'starting' ? 'Get to know the soil-care product and the vegetative feed. Follow the directions for your plant and application; newly planted gardens do not all need the same routine.' : 'The stage feed and the soil-care product have different jobs. Explore each one to decide what fits your growing medium and current routine.';
      root.querySelector('[data-gg-recommendations]').innerHTML = state.recommendations.map(key => { const p = byKey(key); return `<a href="${esc(p.url)}" class="ggx-recommendation" style="background:${esc(p.tint)}"><img src="${esc(p.image)}" alt="${esc(p.name)} packaging" width="100" height="130"><div><small>${esc(p.role)}</small><strong>${esc(p.name)}</strong><span>Meet the product <svg class="gg-ui-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="display:inline-block;width:1em;height:1em;flex-shrink:0;vertical-align:-.125em;overflow:visible"><path d="M4 20 20 4M8 4h12v12"/></svg></span></div></a>`; }).join('');
      showStep(2);
    }
    root.querySelectorAll('[data-gg-stage-answer]').forEach(button => button.addEventListener('click', () => { state.stage = button.dataset.ggStageAnswer; showStep(1); }));
    root.querySelectorAll('[data-gg-purpose-answer]').forEach(button => button.addEventListener('click', () => { state.purpose = button.dataset.ggPurposeAnswer; recommend(); }));
    root.querySelector('[data-gg-finder-back]').addEventListener('click', () => showStep(0));
    root.querySelector('[data-gg-finder-restart]').addEventListener('click', () => { state.stage = ''; state.purpose = ''; showStep(0); });
    root.querySelector('[data-gg-use-recommendations]').addEventListener('click', () => {
      state.editing = false;
      choose(state.recommendations,'recommended');
      const collections = root.querySelector('[data-gg-collection-choices]');
      if (collections) collections.open = false;
      navigateView('routine');
    });
    addButton.addEventListener('click', async () => {
      if (state.busy) return;
      const lines = selected();
      if (!lines.length || lines.some(line => !line.variant?.available)) { renderBuilder(); return; }
      const items = lines.map(line => ({id:String(line.variant.id),quantity:1}));
      state.busy = true;
      root.setAttribute('aria-busy','true');
      const controls = [...root.querySelectorAll('button,input,select')];
      const disabledBefore = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      renderBuilder();
      try { await addItems(root,items); }
      catch (error) {
        state.busy = false;
        root.removeAttribute('aria-busy');
        controls.forEach((control,i) => { control.disabled = disabledBefore[i]; });
        renderBuilder();
        status.textContent = error.message || 'Could not add your selected products. Please check your bag and try again.';
        status.classList.add('is-error');
        status.tabIndex = -1;
        status.focus({preventScroll:true});
      }
    });
    root.classList.add('is-enhanced');
    const stageParam = new URLSearchParams(location.search).get('stage');
    const stage = {starting:'starting',growing:'growing',growth:'growing',flowering:'flowering',flower:'flowering'}[stageParam];
    if (stage) { state.stage = stage; showStep(1,false); } else showStep(0,false);
    renderBuilder();
    const viewFromUrl = () => location.hash === '#finder' ? 'finder' : location.hash === '#routine' ? 'routine' : stage ? 'finder' : 'routine';
    const paths = root.querySelector('[data-gg-studio-paths]');
    if (paths) paths.hidden = false;
    showView(viewFromUrl(),{scroll:['#finder','#routine'].includes(location.hash),instant:true});
    // Keep one set of answers, selected products and sizes while the URL gives
    // each path its own entry point and browser-history position.
    const onStudioLink = event => {
      if (!root.isConnected) { document.removeEventListener('click',onStudioLink); return; }
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href,location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname || (url.search && url.search !== location.search)) return;
      if (!['#finder','#routine'].includes(url.hash)) return;
      event.preventDefault();
      navigateView(url.hash.slice(1));
    };
    const onStudioHistory = () => {
      if (!root.isConnected) {
        window.removeEventListener('popstate',onStudioHistory);
        window.removeEventListener('hashchange',onStudioHistory);
        return;
      }
      const view = viewFromUrl();
      if (view !== state.view) showView(view,{scroll:true,focus:true,instant:true});
    };
    document.addEventListener('click',onStudioLink);
    window.addEventListener('popstate',onStudioHistory);
    window.addEventListener('hashchange',onStudioHistory);
  }
  function setupGuides(root) {
    if (root.dataset.ggExperienceReady) return;
    root.dataset.ggExperienceReady = 'true';
    const cards = [...root.querySelectorAll('[data-gg-guide-card]')];
    const input = root.querySelector('[data-gg-guide-search]');
    const filters = [...root.querySelectorAll('[data-gg-guide-category]')];
    const haystacks = cards.map(card => card.textContent.toLowerCase().replace(/\s+/g,' ').trim());
    const params = new URLSearchParams(location.search);
    let query = params.get('guide_query') || '';
    let category = params.get('guide_category') || 'all';
    if (!filters.some(button => button.dataset.ggGuideCategory === category)) category = 'all';
    let currentPage = Math.max(1,Number.parseInt(params.get('guide_page'),10) || 1);
    const pageSize = 12;
    if (input) input.value = query;
    root.querySelector('[data-gg-guide-controls]').hidden = false;
    function render(updateUrl = false, focusResults = false) {
      const term = query.toLowerCase().trim();
      const matching = cards.filter((card,index) => (category === 'all' || card.dataset.ggGuideTags.split('|').includes(category)) && (!term || haystacks[index].includes(term)));
      const pages = Math.max(1,Math.ceil(matching.length / pageSize));
      currentPage = Math.min(currentPage,pages);
      const visible = new Set(matching.slice((currentPage - 1) * pageSize,currentPage * pageSize));
      cards.forEach(card => { card.hidden = !visible.has(card); });
      filters.forEach(button => button.setAttribute('aria-pressed',String(button.dataset.ggGuideCategory === category)));
      const totalLabel = matching.length === 1 ? 'article' : 'articles';
      const batchLabel = Number(root.dataset.ggSourcePages) > 1 ? ' in this library page' : '';
      root.querySelector('[data-gg-guide-count]').textContent = matching.length ? `${matching.length} ${totalLabel}${batchLabel} · Showing ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize,matching.length)}` : 'No matching articles';
      root.querySelector('[data-gg-guide-empty]').hidden = matching.length > 0;
      root.querySelector('[data-gg-guide-pages]').hidden = pages <= 1;
      root.querySelector('[data-gg-guide-page-label]').textContent = `Page ${currentPage} of ${pages}`;
      root.querySelector('[data-gg-guide-prev]').disabled = currentPage <= 1;
      root.querySelector('[data-gg-guide-next]').disabled = currentPage >= pages;
      const featured = root.querySelector('[data-gg-guide-feature]');
      if (featured) featured.hidden = Boolean(term) || category !== 'all' || currentPage !== 1;
      if (updateUrl) {
        const url = new URL(location.href);
        [['guide_query',query],['guide_category',category === 'all' ? '' : category],['guide_page',currentPage > 1 ? String(currentPage) : '']].forEach(([name,value]) => { if(value) url.searchParams.set(name,value); else url.searchParams.delete(name); });
        history.replaceState(null,'',url);
      }
      if (focusResults) {
        scrollTo(root.querySelector('#guide-directory'));
        const count = root.querySelector('[data-gg-guide-count]');
        count.tabIndex = -1;
        count.focus({preventScroll:true});
      }
    }
    filters.forEach(button => button.addEventListener('click', () => { category = button.dataset.ggGuideCategory; currentPage = 1; render(true); }));
    input?.addEventListener('input', () => { query = input.value; currentPage = 1; render(true); });
    root.querySelector('[data-gg-guide-prev]').addEventListener('click', () => { currentPage--; render(true,true); });
    root.querySelector('[data-gg-guide-next]').addEventListener('click', () => { currentPage++; render(true,true); });
    root.querySelector('[data-gg-guide-reset]').addEventListener('click', () => { query = ''; category = 'all'; currentPage = 1; if(input) input.value = ''; render(true); input?.focus({preventScroll:true}); });
    render();
  }
  function setup(scope = document) {
    const range = scope.matches?.('[data-gg-range]') ? [scope] : scope.querySelectorAll('[data-gg-range]');
    const studio = scope.matches?.('[data-gg-studio]') ? [scope] : scope.querySelectorAll('[data-gg-studio]');
    const guides = scope.matches?.('[data-gg-guide-directory]') ? [scope] : scope.querySelectorAll('[data-gg-guide-directory]');
    guides.forEach(setupGuides);
    range.forEach(setupRange);
    studio.forEach(setupStudio);
  }
  window.GGCommerceExperience = {setup};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => setup()); else setup();
  document.addEventListener('shopify:section:load',event => setup(event.target));
})();
